import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ChatService } from './chat.service';
import { ChatSignalRService } from './chat-signal-r.service';
import { HubConnectionState } from '@microsoft/signalr';
import { 
    ConversationResponse, 
    ChatMessageResponse, 
    SendMessageRequest, 
    ChatMessageTypeEnum,
    UserPresencePayload,
    TypingNotificationPayload,
    ReadReceiptPayload
} from '../../models/chat/chat-api.models';

@Injectable({
  providedIn: 'root'
})
export class ChatStateService {
  // State Subjects
  private _conversations = new BehaviorSubject<ConversationResponse[]>([]);
  private _activeConversation = new BehaviorSubject<ConversationResponse | null>(null);
  private _messages = new BehaviorSubject<ChatMessageResponse[]>([]);
  private _pinnedMessages = new BehaviorSubject<ChatMessageResponse[]>([]);
  private _userPresenceState = new BehaviorSubject<Map<string, { presenceStatus: number, lastSeen: string | null }>>(new Map());
  private _typingUsers = new BehaviorSubject<Map<number, Set<string>>>(new Map()); // ConversationId -> Set<UserId>
  
  // Expose as Observables
  public conversations$ = this._conversations.asObservable();
  public activeConversation$ = this._activeConversation.asObservable();
  public messages$ = this._messages.asObservable();
  public pinnedMessages$ = this._pinnedMessages.asObservable();
  public userPresence$ = this._userPresenceState.asObservable();
  public typingUsers$ = this._typingUsers.asObservable();
  
  // Expose Total Unread Chats Count (Number of conversations with unread messages)
  public totalUnreadCount$ = this._conversations.pipe(
      map((list: ConversationResponse[]) => list.filter(c => (c.unreadCount || 0) > 0).length)
  );

  private currentUserId: string | null = null;
  private hasInitiallyLoaded = false;

  constructor(
    private chatService: ChatService,
    private signalRService: ChatSignalRService,
    private ngZone: NgZone 
  ) {
      // Connect is now explicitly called by top-nav-bar components during ngOnInit
  }

  private getCurrentUserId(): string | null {
      try {
          const account = localStorage.getItem('account');
          if (!account) return null;
          const parsed = JSON.parse(account);
          return parsed.id || parsed.Id || parsed.userId || parsed.UserId || null;
      } catch (e) {
          console.error('Error parsing account for ID', e);
          return null;
      }
  }

  public connect() {
      const newUserId = this.getCurrentUserId();
      if (!newUserId) return;

      if (this.currentUserId === newUserId && this.signalRService.connectionState$.value === HubConnectionState.Connected) {
          return; // Already connected
      }

      this.currentUserId = newUserId;
      this.setupSubscriptions(); // Safe to call multiple times with boolean flag
      
      this.signalRService.startConnection().catch(err => console.error('SignalR Init Error', err));
      this.hasInitiallyLoaded = false;
      this.loadConversations();
  }

  public disconnect() {
      this.signalRService.stopConnection();
      this.currentUserId = null;
      this.hasInitiallyLoaded = false;
      this._conversations.next([]);
      this._activeConversation.next(null);
      this._messages.next([]);
      this._userPresenceState.next(new Map());
      this._typingUsers.next(new Map());
  }

  // --- Initialization ---

  private isSignalRInitialized = false;

  private setupSubscriptions() {
      if (this.isSignalRInitialized) return;
      this.isSignalRInitialized = true;

      // 0. Auto-Join Groups upon Connection
      this.signalRService.connectionState$.subscribe(state => {
          if (state === HubConnectionState.Connected) { 
             this.joinAllConversations();
          }
      });

      // 2. Incoming Message
      this.signalRService.messageReceived.subscribe(msg => {
          this.ngZone.run(() => this.handleIncomingMessage(msg));
      });

      // 3. Conversation Updated
      this.signalRService.conversationUpdated.subscribe(conv => {
          this.ngZone.run(() => this.handleConversationUpdate(conv));
      });

      // 4. Presence
      this.signalRService.userPresence.subscribe(p => {
          this.ngZone.run(() => this.updatePresence(p));
      });

      // 5. Typing
      this.signalRService.userTyping.subscribe(t => {
          this.ngZone.run(() => this.updateTyping(t));
      });

      // 6. Read Receipts
      this.signalRService.messagesRead.subscribe(r => {
          this.ngZone.run(() => this.updateReadReceipts(r));
      });

      // 7. Pinned Messages
      this.signalRService.messagePinnedUpdated.subscribe(msg => {
          this.ngZone.run(() => this.handlePinnedMessageUpdate(msg));
      });
  }
  
  private joinAllConversations() {
      const list = this._conversations.value;
      list.forEach((c: ConversationResponse) => {
          this.signalRService.joinConversation(c.id);
      });
  }

  // --- API Actions ---

  public loadConversations() {
    if (this.hasInitiallyLoaded && this._conversations.value.length > 0) {
        return; // Already primed globally 
    }
    this.hasInitiallyLoaded = true;

    this.chatService.getConversations().subscribe((data) => {
         const list = Array.isArray(data) ? data : (data as any).result || [];
         
         // Sort by last message time
         const processed = list.map((c: any) => this.processConversation(c));
         processed.sort((a: ConversationResponse, b: ConversationResponse) => {
             const timeA = a.lastMessageOn ? new Date(a.lastMessageOn).getTime() : 0;
             const timeB = b.lastMessageOn ? new Date(b.lastMessageOn).getTime() : 0;
             return timeB - timeA;
         });
         this._conversations.next(processed);

         // Join SignalR Groups for ALL conversations to receive real-time updates (typing, etc.)
         processed.forEach((c: ConversationResponse) => {
             this.signalRService.joinConversation(c.id);
             
             if (c.participants) {
                 const presenceMap = this._userPresenceState.value;
                 let changed = false;
                 c.participants.forEach(p => {
                     if (p.presenceStatus !== undefined) {
                         presenceMap.set(p.userId.toLowerCase(), { presenceStatus: p.presenceStatus, lastSeen: p.lastSeen || null });
                         changed = true;
                     }
                 });
                 if (changed) {
                     this._userPresenceState.next(new Map(presenceMap));
                 }
             }
         });
    });
  }

  public clearActiveConversation() {
      // We do NOT leave the group anymore, so we keep receiving typing/updates in sidebar
      this._activeConversation.next(null);
      this._messages.next([]);
      this._pinnedMessages.next([]);
  }

  public selectConversation(conv: ConversationResponse) {
      const currentActive = this._activeConversation.value;
      if (currentActive?.id === conv.id) return;

      // Ensure we are joined (idempotent usually, but good to be sure)
      this.signalRService.joinConversation(conv.id);

      this._activeConversation.next(conv);
      this._messages.next([]); // Clear old messages
      this._pinnedMessages.next([]); // Clear old pinned messages

      // Load Messages
      this.chatService.getMessages(conv.id).subscribe((data) => {
          const list = Array.isArray(data) ? data : (data as any).result || [];
          const processed = list.map((m: any) => this.processMessage(m));
          
          // Sort Chronologically
          processed.sort((a: ChatMessageResponse, b: ChatMessageResponse) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
          this._messages.next(processed);

          // Mark as Read
          if (conv.unreadCount > 0) {
              this.markAsRead(conv.id);
          }
      });

      // Load Pinned Messages
      this.chatService.getPinnedMessages(conv.id).subscribe((data) => {
          const list = Array.isArray(data) ? data : (data as any).result || [];
          const processed = list.map((m: any) => this.processMessage(m));
          processed.sort((a: ChatMessageResponse, b: ChatMessageResponse) => 
              new Date(b.pinnedAt || 0).getTime() - new Date(a.pinnedAt || 0).getTime()
          );
          this._pinnedMessages.next(processed);
      });
  }

  public sendMessage(content: string, file?: File, type: ChatMessageTypeEnum = ChatMessageTypeEnum.Text) {
      const active = this._activeConversation.value;
      if (!active) return;
      if (!file && !content?.trim()) return;

      // Optimistic Update
      const optimisticMsg: ChatMessageResponse = {
          id: -Date.now(), // Temporary ID
          conversationId: active.id,
          senderId: this.currentUserId || 'me',
          senderName: 'Me',
          content: content || (type === ChatMessageTypeEnum.Image ? 'Sent an image' : 'Sent a file'),
          chatMessageType: type,
          sentAt: new Date(),
          isMyMessage: true,
          isRead: false,
          attachmentUrl: null,
          fileName: file ? file.name : undefined
      };
      
      const currentMsgs = this._messages.value;
      this._messages.next([...currentMsgs, optimisticMsg]);

      // Prepare Request
      const req: SendMessageRequest = {
          conversationId: active.id,
          content: content || '',
          messageType: type,
          attachmentUrl: null,
          file: file
      };

      this.chatService.sendChatMessage(req).subscribe({
          next: (response) => {
              const processed = this.processMessage(response);
              const currentMsgs = this._messages.value;
              
              let updatedMsgs: ChatMessageResponse[];
              if (currentMsgs.some(m => m.id === processed.id)) {
                  updatedMsgs = currentMsgs.filter(m => m.id !== optimisticMsg.id);
              } else {
                  updatedMsgs = currentMsgs.map(m => m.id === optimisticMsg.id ? processed : m);
              }
              this._messages.next(updatedMsgs);
              
              // Update Conversation List (Move to top)
              this.updateConversationListInternal(processed);
          },
          error: (err) => {
              console.error('Send failed', err);
              // Remove optimistic message on failure
              const filtered = this._messages.value.filter(m => m.id !== optimisticMsg.id);
              this._messages.next(filtered);
          }
      });
  }

  public markAsRead(convId: number) {
      this.chatService.markAsRead(convId).subscribe(() => {
          const list = this._conversations.value;
          const conv = list.find(c => c.id === convId);
          if (conv) {
              conv.unreadCount = 0;
              this._conversations.next([...list]);
          }
      });
  }

  public sendTyping(convId: number) {
      this.signalRService.sendTyping(convId);
  }

  public sendStoppedTyping(convId: number) {
      this.signalRService.sendStoppedTyping(convId);
  }

  public createConversation(userId: string, initialMsgContent?: string) {
      const req: any = {
          participantUserIds: [userId],
          type: 0 // Individual
      };

      if (initialMsgContent) {
          req.initialMessage = {
              content: initialMsgContent,
              messageType: ChatMessageTypeEnum.Text
          };
      }

      this.chatService.createConversation(req).subscribe(conv => {
          const processed = this.processConversation(conv);
          const list = this._conversations.value;
          
          if (!list.find(c => c.id === processed.id)) {
              this._conversations.next([processed, ...list]);
          }
          this.selectConversation(processed);
      });
  }

  // --- SignalR Handlers ---

  private handleIncomingMessage(msg: ChatMessageResponse) {
      msg = this.processMessage(msg);
      
      const active = this._activeConversation.value;
      const msgConvId = Number(msg.conversationId);
      const activeId = active ? Number(active.id) : null;

      if (activeId !== null && activeId === msgConvId) {
          const current = [...this._messages.value];
          if (!current.some(m => m.id === msg.id)) {
              if (msg.isMyMessage) {
                  const optimisticIndex = current.findIndex(m => m.id < 0 && m.content === msg.content);
                  if (optimisticIndex > -1) {
                      current.splice(optimisticIndex, 1);
                  }
              }
              this._messages.next([...current, msg]);
              
              if (!msg.isMyMessage) {
                  this.markAsRead(msg.conversationId);
              }
          }
      }

      this.updateConversationListInternal(msg);
  }

  private handleConversationUpdate(conv: ConversationResponse) {
      conv = this.processConversation(conv);
      const list = this._conversations.value;
      const index = list.findIndex(c => c.id === conv.id);

      let newList = [...list];
      if (index > -1) {
          newList[index] = conv;
      } else {
          newList.unshift(conv);
      }
      
      // Sort
      newList.sort((a, b) => new Date(b.lastMessageOn).getTime() - new Date(a.lastMessageOn).getTime());
      this._conversations.next(newList);
  }

  private updateConversationListInternal(msg: ChatMessageResponse) {
      const list = [...this._conversations.value];
      const index = list.findIndex(c => c.id == msg.conversationId);
      
      if (index > -1) {
          const conv = { ...list[index] };
          conv.lastMessageContent = msg.content;
          conv.lastMessageOn = msg.sentAt;
          
          // Increment unread if not active chat and not my message
          if (!msg.isMyMessage && this._activeConversation.value?.id !== msg.conversationId) {
              conv.unreadCount = (conv.unreadCount || 0) + 1;
          }

          // Move to top
          list.splice(index, 1);
          this._conversations.next([conv, ...list]);
      } else {
          // If conversation not in list (new), reload
          this.loadConversations();
      }
  }

  private updatePresence(p: UserPresencePayload) {
      const current = new Map(this._userPresenceState.value);
      current.set(p.userId.toLowerCase(), { presenceStatus: p.presenceStatus, lastSeen: p.lastSeen });
      this._userPresenceState.next(current);
  }

  private updateTyping(t: TypingNotificationPayload & { isTyping: boolean }) {
      // Filter out self
      if (this.currentUserId && t.userId && t.userId.toLowerCase() === this.currentUserId.toLowerCase()) {
          return;
      }

      const convId = Number(t.conversationId); // Ensure number
      const current = this._typingUsers.value;
      
      if (!current.has(convId)) {
          current.set(convId, new Set());
      }
      const set = current.get(convId)!;
      
      if (t.isTyping) set.add(t.userId);
      else set.delete(t.userId);
      
      this._typingUsers.next(new Map(current));
  }

  private updateReadReceipts(r: ReadReceiptPayload) {
      const active = this._activeConversation.value;
      if (active && active.id === r.conversationId) {
          const msgs = this._messages.value.map(m => {
              if (r.messageIds.includes(m.id)) {
                  // Mark as read
                  return { ...m, isRead: true, readAt: r.readAt };
              }
              return m;
          });
          this._messages.next(msgs);
      }
  }

  private handlePinnedMessageUpdate(msg: ChatMessageResponse) {
      msg = this.processMessage(msg);
      const active = this._activeConversation.value;
      
      if (active && active.id === msg.conversationId) {
          const currentPinned = this._pinnedMessages.value;
          if (msg.isPinned) {
              const idx = currentPinned.findIndex(m => m.id === msg.id);
              let newList = [...currentPinned];
              if (idx > -1) {
                  newList[idx] = msg;
              } else {
                  newList.unshift(msg);
              }
              newList.sort((a, b) => new Date(b.pinnedAt || 0).getTime() - new Date(a.pinnedAt || 0).getTime());
              this._pinnedMessages.next(newList);
          } else {
              this._pinnedMessages.next(currentPinned.filter(m => m.id !== msg.id));
          }
      }
      
      // Update in main messages list
      const currentMsgs = this._messages.value;
      const msgIdx = currentMsgs.findIndex(m => m.id === msg.id);
      if (msgIdx > -1) {
          const newMsgs = [...currentMsgs];
          newMsgs[msgIdx] = { ...newMsgs[msgIdx], isPinned: msg.isPinned, pinnedAt: msg.pinnedAt, pinnedByName: msg.pinnedByName };
          this._messages.next(newMsgs);
      }
  }

  // --- Helpers ---
  
  private processConversation(c: any): ConversationResponse {
      if (!c) return c;
      if (c.result) c = c.result; // Handle API Wrapper

      if (c.lastMessageOn) c.lastMessageOn = this.toLocal(c.lastMessageOn);
      return c as ConversationResponse;
  }

  private processMessage(m: any): ChatMessageResponse {
      if (!m) return m;
      if (m.result) m = m.result; // Handle API Wrapper

      // Local Time
      if (m.sentAt) m.sentAt = this.toLocal(m.sentAt);

      // Force Number IDs (Just in case API returns strings despite TS interface)
      if (m.id) m.id = Number(m.id);
      if (m.conversationId) m.conversationId = Number(m.conversationId);

      // Recalculate isMyMessage
      if (this.currentUserId && m.senderId) {
          m.isMyMessage = m.senderId.trim().toLowerCase() === this.currentUserId.trim().toLowerCase();
      }

      return m as ChatMessageResponse;
  }

  private toLocal(dateEncoded: string | Date): Date {
      if (!dateEncoded) return new Date();
      if (dateEncoded instanceof Date) return dateEncoded;
      if (typeof dateEncoded === 'string' && !dateEncoded.endsWith('Z') && !dateEncoded.includes('+')) {
          return new Date(dateEncoded + 'Z');
      }
      return new Date(dateEncoded);
  }

}
